import React, { useEffect, useState } from "react";
import { db, auth, storage } from "../firebase";
import { collection, query, where, getDocs, updateDoc, doc, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useBuilding } from "../context/BuildingContext";
import { useEffectiveIdentity, useIsReadOnly } from "../context/EffectiveIdentityContext";
import { useToast } from "../components/Toast";
import { formatCurrencyUY, formatDateLocalized } from "../utils/formatters";
import { tolerantId } from "../utils/firestoreHelpers";
import { leerFichaResidente } from "../utils/fichaResidente";
import { claveDeId, leerPropiasDelEdificio, useridsDeLaCuenta } from "../utils/consultasPropias";
import {
  FaGavel,
  FaChevronDown,
  FaChevronUp,
  FaCloudUploadAlt,
  FaTimes,
  FaExclamationCircle,
  FaInfoCircle,
  FaFileAlt,
} from "react-icons/fa";
import { useIsMobile } from "../hooks/useResponsive";
import { AppScreen, AppHeader, AppCard, AppEmptyState, Badge, AppButton, IconBox } from "../components/app";
import { appColors, appType, appShadows, appRadius } from "../utils/appTheme";
import { useTranslation } from "react-i18next";

// Configuración de estilos por estado (las etiquetas visibles se traducen con t()).
const statusConfig = {
  activa: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
  pagada: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  anulada: { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb" },
  incluida_en_cierre: { bg: "#eff6ff", color: "#2563eb", border: "#bfdbfe" },
};

const appealStatusConfig = {
  pending: { bg: "#fffbeb", color: "#b45309", border: "#fcd34d" },
  accepted: { bg: "#f0fdf4", color: "#16a34a", border: "#bbf7d0" },
  rejected: { bg: "#fef2f2", color: "#dc2626", border: "#fecaca" },
};

const getStatusStyle = (status) => {
  const key = (status || "activa").toLowerCase();
  return statusConfig[key] || statusConfig.activa;
};

const formatDate = (val) => {
  if (!val) return "—";
  if (val?.seconds) return formatDateLocalized(new Date(val.seconds * 1000));
  const d = new Date(val);
  return isNaN(d.getTime()) ? "—" : formatDateLocalized(d);
};

// Tono de estado en la paleta app (mobile)
const statusTone = {
  activa: appColors.red,
  pagada: appColors.green,
  anulada: appColors.ink3,
  incluida_en_cierre: appColors.blue,
};

const MultasResidente = ({ setSelected, goBack }) => {
  const { selectedBuilding } = useBuilding();
  const { email: effEmail, userid: effUserid, viewingAs } = useEffectiveIdentity();
  // "Ver como residente": modo solo lectura. Bloquea la apelación de multas.
  const readOnly = useIsReadOnly();
  // El ex-ocupante (edificio de solo lectura) tampoco apela: las reglas solo
  // dejan apelar al residente vigente.
  const exOcupante = !!selectedBuilding?.isReadOnly;
  const soloLectura = readOnly || exOcupante;
  const isMobile = useIsMobile();
  const { showToast } = useToast();
  const { t } = useTranslation("multasResidente");
  const [multas, setMultas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // Estado para modal de apelación
  const [appealModal, setAppealModal] = useState(null);
  const [appealReason, setAppealReason] = useState("");
  const [appealFile, setAppealFile] = useState(null);
  const [submittingAppeal, setSubmittingAppeal] = useState(false);

  useEffect(() => {
    const fetchMultas = async () => {
      if (!selectedBuilding?.buildingid) return;
      setLoading(true);
      try {
        if (!effEmail) return;

        // Ficha del residente. En "ver como" sale del contexto: quien mira es
        // staff y con C10 no lista users por email.
        const ficha = await leerFichaResidente(db, {
          email: effEmail,
          userid: effUserid,
          viendoComo: readOnly,
          nombre: viewingAs?.name,
        });
        const userId = ficha ? ficha.userid : auth.currentUser?.uid;
        const normalizedUserId = String(userId);

        // Obtener las unidades del usuario en este edificio
        const aptsQ = query(
          collection(db, "apartments"),
          where("building", "in", tolerantId(selectedBuilding.buildingid))
        );
        const aptsSnap = await getDocs(aptsQ);
        const userUnitIds = new Set();
        const uidStr = normalizedUserId;
        const uidNum = Number(normalizedUserId);
        const matchUser = (val) =>
          val != null && (String(val) === uidStr || (!isNaN(uidNum) && Number(val) === uidNum));
        aptsSnap.docs.forEach((d) => {
          const apt = d.data();
          if (matchUser(apt.owner) || matchUser(apt.ownerSecondary) || matchUser(apt.tenant) || matchUser(apt.tenantSecondary)) {
            userUnitIds.add(d.id);
          }
        });

        // Multas del edificio (el vigente y el staff en "ver como" las leen
        // todas y se filtran acá). El ex-ocupante solo lee las suyas (C12): su
        // consulta fija también userid o responsibleUserId, con los userids de
        // su cuenta (user_access) o, en "ver como", el del residente mirado.
        const propios = new Set([claveDeId(userId)]);
        const uidSesion = readOnly ? null : auth.currentUser?.uid || null;
        const all = await leerPropiasDelEdificio(db, "multas", selectedBuilding.buildingid, {
          soloPropias: exOcupante,
          campos: ["userid", "responsibleUserId"],
          propios: async () => {
            const ids = await useridsDeLaCuenta(db, { uid: uidSesion, respaldo: [userId] });
            ids.forEach((id) => propios.add(claveDeId(id)));
            return ids;
          },
        });

        // Filtrar por responsable (responsibleUserId, o si no el userid) o por
        // unidad del usuario.
        const userMultas = all.filter((m) => {
          const mUserId = claveDeId(m.responsibleUserId || m.userid || m.userId);
          if (mUserId !== "" && propios.has(mUserId)) return true;
          if (m.unitId && userUnitIds.has(m.unitId)) return true;
          return false;
        });

        // Ordenar por createdAt descendente
        userMultas.sort((a, b) => {
          const da = a.createdAt?.seconds || 0;
          const db2 = b.createdAt?.seconds || 0;
          return db2 - da;
        });

        setMultas(userMultas);
      } catch (e) {
        console.error("Error fetching multas:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchMultas();
  }, [selectedBuilding, effEmail, effUserid, readOnly, exOcupante, viewingAs?.name]);

  // Manejo del archivo de evidencia
  const handleAppealFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      showToast(t("toast.fileTooLarge"), "error");
      return;
    }
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      showToast(t("toast.fileType"), "error");
      return;
    }
    setAppealFile(file);
  };

  // Enviar apelación
  const handleSubmitAppeal = async () => {
    if (soloLectura) { showToast(t("toast.readOnly")); return; }
    if (!appealModal) return;
    if (!appealReason.trim() || appealReason.trim().length < 20) {
      showToast(t("toast.minChars"), "error");
      return;
    }

    setSubmittingAppeal(true);
    try {
      // Subir archivo si existe
      let attachmentUrl = null;
      if (appealFile) {
        const ext = appealFile.name.split(".").pop();
        const storagePath = `multas/appeals/${appealModal.id}/${Date.now()}.${ext}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, appealFile);
        attachmentUrl = await getDownloadURL(storageRef);
      }

      // Actualizar documento en Firestore
      await updateDoc(doc(db, "multas", appealModal.id), {
        appealReason: appealReason.trim(),
        appealDate: Timestamp.now(),
        appealStatus: "pending",
        appealAttachmentUrl: attachmentUrl,
        appealedBy: auth.currentUser?.email || "",
      });

      // Actualizar estado local
      setMultas((prev) =>
        prev.map((m) =>
          m.id === appealModal.id
            ? { ...m, appealReason: appealReason.trim(), appealDate: Timestamp.now(), appealStatus: "pending", appealAttachmentUrl: attachmentUrl }
            : m
        )
      );

      showToast(t("toast.appealSent"), "success");
      setAppealModal(null);
      setAppealReason("");
      setAppealFile(null);
    } catch (e) {
      console.error("Error enviando apelación:", e);
      showToast(t("toast.appealError"), "error");
    } finally {
      setSubmittingAppeal(false);
    }
  };

  const canAppeal = (m) => !soloLectura && m.status === "activa" && m.appealStatus !== "pending";

  // Etiquetas de estado traducidas (la config de estilos vive en statusConfig/appealStatusConfig).
  const validStatus = ["activa", "pagada", "anulada", "incluida_en_cierre"];
  const statusLabel = (status) => {
    const key = (status || "activa").toLowerCase();
    return t("status." + (validStatus.includes(key) ? key : "activa"));
  };
  const appealStatusLabel = (appealStatus) =>
    appealStatus ? t("appealStatus." + appealStatus) : null;

  if (loading) {
    if (isMobile) {
      return (
        <AppScreen
          header={
            <AppHeader
              title={t("title")}
              subtitle={
                selectedBuilding?.name
                  ? t("subtitleBuilding", { name: selectedBuilding.name })
                  : t("subtitleDefault")
              }
              onBack={goBack}
            />
          }
        >
          <p style={{ ...appType.bodySm, textAlign: "center", padding: 40 }}>
            {t("loadingMultas")}
          </p>
        </AppScreen>
      );
    }
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
        <div style={{ width: 40, height: 40, border: `4px solid ${appColors.line2}`, borderTop: `4px solid ${appColors.blue}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
      </div>
    );
  }

  // ─── Contenido mobile: estilo app "Multas" (screen 08) ──────────────────────
  const badgeTone = (status) =>
    status === "pagada"
      ? "green"
      : status === "anulada"
      ? "ink3"
      : status === "incluida_en_cierre"
      ? "blue"
      : "red";

  const mobileContent = (
    <AppScreen
      header={
        <AppHeader
          title={t("title")}
          subtitle={
            selectedBuilding?.name
              ? t("subtitleBuilding", { name: selectedBuilding.name })
              : t("subtitleDefault")
          }
          onBack={goBack}
        />
      }
    >
      {multas.length === 0 ? (
        <AppEmptyState
          icon={<FaGavel />}
          title={t("emptyTitle")}
          description={t("emptyDescription")}
        />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {multas.map((m) => {
            const st = getStatusStyle(m.status);
            const tone = statusTone[(m.status || "activa").toLowerCase()] || statusTone.activa;
            const isExpanded = expandedId === m.id;
            const appealSt = m.appealStatus ? appealStatusConfig[m.appealStatus] : null;
            return (
              <AppCard
                key={m.id}
                accent="blue"
                padding={0}
                style={{ borderLeftColor: tone, overflow: "hidden" }}
              >
                {/* Cabecera: icono doc + micro-label SANCIÓN + título + badge de estado */}
                <div
                  style={{ padding: "16px 16px 0", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 12 }}
                  onClick={() => setExpandedId(isExpanded ? null : m.id)}
                >
                  <IconBox icon={<FaFileAlt />} color={tone} size={40} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ ...appType.statLabel, color: appColors.red, marginBottom: 3 }}>{t("sanctionLabel")}</div>
                    <div style={{ ...appType.cardTitle, fontSize: 15, overflowWrap: "anywhere" }}>
                      {m.reason || t("noReason")}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, minWidth: 0 }}>
                    <Badge tone={badgeTone(m.status)} dot>
                      {statusLabel(m.status)}
                    </Badge>
                    {appealSt && <Badge tone="amber" dot>{appealStatusLabel(m.appealStatus)}</Badge>}
                  </div>
                </div>

                {/* Caja de IMPORTE resaltada (fondo frío cream) */}
                <div style={{ padding: "12px 16px 0" }}>
                  <div
                    onClick={() => setExpandedId(isExpanded ? null : m.id)}
                    style={{ background: appColors.cream, borderRadius: appRadius.tile, padding: "12px 14px", cursor: "pointer" }}
                  >
                    <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 4 }}>{t("amountLabel")}</div>
                    <div style={{ ...appType.stat, color: appColors.red }}>
                      {formatCurrencyUY(m.amount || 0)}
                    </div>
                  </div>
                </div>

                {/* Footer en grilla: FECHA / UNIDAD */}
                <div
                  onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  style={{ padding: "12px 16px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, cursor: "pointer" }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>{t("dateLabel")}</div>
                    <div style={{ ...appType.body, fontSize: 13.5, fontVariantNumeric: "tabular-nums" }}>
                      {formatDate(m.createdAt)}
                    </div>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>{t("unitLabel")}</div>
                    <div style={{ ...appType.body, fontSize: 13.5, overflowWrap: "anywhere" }}>
                      {m.unitNumber ? m.unitNumber : "—"}
                    </div>
                  </div>
                </div>

                {/* Detalle expandido */}
                {isExpanded && (
                  <div style={{ padding: "0 16px 16px", borderTop: `1px solid ${appColors.line2}` }}>
                    <div style={{ padding: "14px 0 0", display: "flex", flexDirection: "column", gap: 10 }}>
                      {m.legalReference && (
                        <div style={{ minWidth: 0 }}>
                          <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>{t("legalReferenceLabel")}</div>
                          <div style={{ ...appType.body, fontSize: 13.5, overflowWrap: "anywhere" }}>{m.legalReference}</div>
                        </div>
                      )}
                      {m.feedsReserveFund && (
                        <div style={{ minWidth: 0 }}>
                          <div style={{ ...appType.statLabel, color: appColors.ink3, marginBottom: 3 }}>{t("destinationLabel")}</div>
                          <div style={{ ...appType.body, fontSize: 13.5 }}>{t("reserveFund")}</div>
                        </div>
                      )}
                      {m.status === "anulada" && m.annulledReason && (
                        <div
                          style={{ background: appColors.surface2, borderRadius: appRadius.sm, padding: "10px 12px", display: "flex", gap: 8, alignItems: "flex-start", minWidth: 0 }}
                        >
                          <FaInfoCircle size={13} color={appColors.ink2} style={{ flexShrink: 0, marginTop: 2 }} />
                          <span style={{ ...appType.bodySm, color: appColors.ink2, overflowWrap: "anywhere" }}>
                            {t("annulledLabel")} {m.annulledReason}
                            {m.annulledAt ? ` — ${formatDate(m.annulledAt)}` : ""}
                          </span>
                        </div>
                      )}
                      {m.appealStatus && (
                        <div
                          style={{ background: appColors.surface2, borderRadius: appRadius.sm, padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}
                        >
                          <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                            <FaExclamationCircle
                              size={13}
                              color={m.appealStatus === "rejected" ? appColors.red : appColors.amber}
                              style={{ flexShrink: 0, marginTop: 2 }}
                            />
                            <span style={{ ...appType.bodySm, color: appColors.ink2, overflowWrap: "anywhere" }}>
                              {t("appealLabel")} {m.appealReason}
                            </span>
                          </div>
                          {m.appealDate && (
                            <span style={{ ...appType.caption, marginLeft: 21 }}>
                              {t("sentOn", { date: formatDate(m.appealDate) })}
                            </span>
                          )}
                          {m.appealStatus === "rejected" && m.appealRejectionReason && (
                            <span style={{ ...appType.caption, color: appColors.red, marginLeft: 21, overflowWrap: "anywhere" }}>
                              {t("rejectionReason", { reason: m.appealRejectionReason })}
                            </span>
                          )}
                          {m.appealAttachmentUrl && (
                            <a
                              href={m.appealAttachmentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              style={{ ...appType.caption, color: appColors.blue, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5, marginLeft: 21 }}
                            >
                              <FaFileAlt size={10} /> {t("viewAttachment")}
                            </a>
                          )}
                        </div>
                      )}
                      {canAppeal(m) && (
                        <AppButton
                          block
                          variant="primary"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAppealModal(m);
                          }}
                          icon={<FaExclamationCircle size={13} />}
                        >
                          {t("appealFine")}
                        </AppButton>
                      )}
                    </div>
                  </div>
                )}
              </AppCard>
            );
          })}
        </div>
      )}
    </AppScreen>
  );

  return (
    <>
    {isMobile ? mobileContent : (
    <div style={{ padding: 24, fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div style={{ width: 42, height: 42, borderRadius: 10, backgroundColor: "#0080ff", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
          <FaGavel />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: "#0080ff" }}>{t("title")}</h1>
          <p style={{ margin: "2px 0 0 0", fontSize: 13, color: "#6b7280" }}>{t("subtitleDesktopUnit")}</p>
        </div>
      </div>

      {/* Contenido */}
      {multas.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 16, padding: "48px 24px", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
          <FaGavel style={{ fontSize: 40, color: "#d1d5db", marginBottom: 12 }} />
          <p style={{ fontSize: 15, color: "#6b7280", margin: 0 }}>{t("emptyTitle")}</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {multas.map((m) => {
            const st = getStatusStyle(m.status);
            const isExpanded = expandedId === m.id;
            const appealSt = m.appealStatus ? appealStatusConfig[m.appealStatus] : null;

            return (
              <div key={m.id} style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", border: "1px solid #f3f4f6", overflow: "hidden" }}>
                {/* Card header - clickeable para expandir */}
                <div
                  style={{ padding: "16px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}
                  onClick={() => setExpandedId(isExpanded ? null : m.id)}
                >
                  {/* Indicador lateral de color */}
                  <div style={{ width: 4, height: 40, borderRadius: 2, background: st.color, flexShrink: 0 }} />

                  {/* Info principal */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                      <span style={{ fontSize: 14, color: "#1a1a2e" }}>{m.reason || t("noReason")}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#9ca3af" }}>
                      <span>{formatDate(m.createdAt)}</span>
                      {m.legalReference && <span>· {m.legalReference}</span>}
                      {m.unitNumber && <span>· {t("unit", { number: m.unitNumber })}</span>}
                    </div>
                  </div>

                  {/* Badges */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {appealSt && (
                      <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 400, background: appealSt.bg, color: appealSt.color, border: `1px solid ${appealSt.border}` }}>
                        {appealStatusLabel(m.appealStatus)}
                      </span>
                    )}
                    <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 400, background: st.bg, color: st.color, border: `1px solid ${st.border}` }}>
                      {statusLabel(m.status)}
                    </span>
                  </div>

                  {/* Monto */}
                  <span style={{ fontSize: 16, fontWeight: 400, color: "#dc2626", flexShrink: 0, marginLeft: 8 }}>
                    {formatCurrencyUY(m.amount || 0)}
                  </span>

                  {/* Chevron */}
                  <div style={{ color: "#9ca3af", flexShrink: 0 }}>
                    {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                  </div>
                </div>

                {/* Detalle expandido */}
                {isExpanded && (
                  <div style={{ padding: "0 20px 16px", borderTop: "1px solid #f3f4f6" }}>
                    <div style={{ padding: "12px 0", display: "flex", flexDirection: "column", gap: 10 }}>
                      {/* Info detallada */}
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13 }}>
                        <div><span style={{ color: "#9ca3af" }}>{t("dateLabel")}: </span><span style={{ color: "#374151" }}>{formatDate(m.createdAt)}</span></div>
                        <div><span style={{ color: "#9ca3af" }}>{t("montoLabel")}: </span><span style={{ color: "#374151" }}>{formatCurrencyUY(m.amount || 0)}</span></div>
                        {m.legalReference && (
                          <div><span style={{ color: "#9ca3af" }}>{t("legalReferenceLabel")}: </span><span style={{ color: "#374151" }}>{m.legalReference}</span></div>
                        )}
                        {m.feedsReserveFund && (
                          <div><span style={{ color: "#9ca3af" }}>{t("destinationLabel")}: </span><span style={{ color: "#374151" }}>{t("reserveFund")}</span></div>
                        )}
                      </div>

                      {/* Info de anulación */}
                      {m.status === "anulada" && m.annulledReason && (
                        <div style={{ background: "#f9fafb", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
                          <FaInfoCircle size={12} color="#6b7280" style={{ marginRight: 6 }} />
                          <span style={{ color: "#6b7280" }}>{t("annulledLabel")} {m.annulledReason}</span>
                          {m.annulledAt && <span style={{ color: "#9ca3af" }}> — {formatDate(m.annulledAt)}</span>}
                        </div>
                      )}

                      {/* Info de apelación previa */}
                      {m.appealStatus && (
                        <div style={{ background: m.appealStatus === "rejected" ? "#fef2f2" : "#fffbeb", borderRadius: 8, padding: "10px 12px", fontSize: 13 }}>
                          <FaExclamationCircle size={12} color={m.appealStatus === "rejected" ? "#dc2626" : "#b45309"} style={{ marginRight: 6 }} />
                          <strong style={{ color: "#374151" }}>{t("appealLabel")} </strong>
                          <span style={{ color: "#374151" }}>{m.appealReason}</span>
                          {m.appealDate && <div style={{ color: "#9ca3af", fontSize: 12, marginTop: 4 }}>{t("sentOn", { date: formatDate(m.appealDate) })}</div>}
                          {m.appealStatus === "rejected" && m.appealRejectionReason && (
                            <div style={{ marginTop: 6, color: "#dc2626", fontSize: 12 }}>
                              {t("rejectionReason", { reason: m.appealRejectionReason })}
                            </div>
                          )}
                          {m.appealAttachmentUrl && (
                            <div style={{ marginTop: 6 }}>
                              <a href={m.appealAttachmentUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#0080ff", fontSize: 12, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <FaFileAlt size={10} /> {t("viewAttachment")}
                              </a>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Botón apelar */}
                      {canAppeal(m) && (
                        <div style={{ paddingTop: 4 }}>
                          <button
                            onClick={(e) => { e.stopPropagation(); setAppealModal(m); }}
                            style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #fcd34d", background: "#fffbeb", color: "#b45309", fontSize: 13, fontWeight: 400, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, transition: "all 0.15s" }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "#fef3c7"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "#fffbeb"; }}
                          >
                            <FaExclamationCircle size={12} />
                            {t("appealFine")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

    </div>
    )}
      {/* Modal de apelación — versión mobile (paleta app) */}
      {appealModal && isMobile && (
        <div style={{ position: "fixed", inset: 0, background: appColors.scrim, display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 1000 }} onClick={() => { setAppealModal(null); setAppealReason(""); setAppealFile(null); }}>
          <div style={{ background: appColors.surface, borderTopLeftRadius: 22, borderTopRightRadius: 22, width: "100%", maxWidth: 480, maxHeight: "92vh", overflowY: "auto", boxShadow: appShadows.hero, paddingBottom: "env(safe-area-inset-bottom, 0px)" }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: "18px 20px 14px", borderBottom: `1px solid ${appColors.line2}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: appColors.surface2, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaGavel size={16} color={appColors.amber} />
                </div>
                <h3 style={{ ...appType.cardTitle, margin: 0 }}>{t("appealFine")}</h3>
              </div>
              <button onClick={() => { setAppealModal(null); setAppealReason(""); setAppealFile(null); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                <FaTimes size={16} color={appColors.ink3} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 20px" }}>
              {/* Resumen de la multa */}
              <div style={{ background: appColors.surface2, borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
                <div style={{ ...appType.body, fontSize: 13, marginBottom: 4 }}>{t("motiveLabel")}: {appealModal.reason}</div>
                <div style={{ ...appType.body, fontSize: 13, marginBottom: 4 }}>{t("montoLabel")}: {formatCurrencyUY(appealModal.amount || 0)}</div>
                <div style={{ ...appType.bodySm, color: appColors.ink3 }}>{formatDate(appealModal.createdAt)}{appealModal.legalReference ? ` · ${appealModal.legalReference}` : ""}</div>
              </div>

              {/* Info */}
              <div style={{ background: appColors.surface2, border: `1px solid ${appColors.line2}`, borderRadius: 12, padding: "10px 12px", marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <FaInfoCircle size={14} color={appColors.blue} style={{ flexShrink: 0, marginTop: 1 }} />
                <span style={{ ...appType.bodySm, color: appColors.ink2 }}>{t("appealInfo")}</span>
              </div>

              {/* Textarea */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ ...appType.bodySm, color: appColors.ink2, display: "block", marginBottom: 6 }}>
                  {t("justificationLabel")}
                </label>
                <textarea
                  value={appealReason}
                  onChange={(e) => setAppealReason(e.target.value)}
                  placeholder={t("justificationPlaceholder")}
                  rows={4}
                  style={{ width: "100%", padding: "10px 12px", border: `1px solid ${appColors.line}`, borderRadius: 12, fontSize: 13.5, fontFamily: appType.fontFamily, color: appColors.ink, resize: "vertical", boxSizing: "border-box", outline: "none" }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = appColors.blue; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = appColors.line; }}
                />
                <span style={{ ...appType.caption, color: appealReason.trim().length >= 20 ? appColors.green : appColors.ink3 }}>
                  {t("minChars", { n: appealReason.trim().length })}
                </span>
              </div>

              {/* Upload de evidencia */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ ...appType.bodySm, color: appColors.ink2, display: "block", marginBottom: 6 }}>
                  {t("evidenceLabel")}
                </label>
                {appealFile ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", border: `1px solid ${appColors.line}`, borderRadius: 12, background: appColors.surface2, minWidth: 0 }}>
                    <FaFileAlt size={14} color={appColors.blue} />
                    <span style={{ ...appType.body, fontSize: 13, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {appealFile.name}
                    </span>
                    <button onClick={() => setAppealFile(null)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                      <FaTimes size={12} color={appColors.ink2} />
                    </button>
                  </div>
                ) : (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px", border: `1px dashed ${appColors.line}`, borderRadius: 12, cursor: "pointer", background: appColors.surface2, color: appColors.ink2, fontSize: 13 }}>
                    <FaCloudUploadAlt size={16} color={appColors.ink2} />
                    <span style={{ ...appType.bodySm }}>{t("attachEvidence")}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleAppealFileChange} style={{ display: "none" }} />
                  </label>
                )}
                <span style={{ ...appType.caption }}>{t("maxSize")}</span>
              </div>

              {/* Acciones */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <AppButton
                  block
                  variant="primary"
                  onClick={handleSubmitAppeal}
                  disabled={submittingAppeal || appealReason.trim().length < 20}
                >
                  {submittingAppeal ? t("sending") : t("submitAppeal")}
                </AppButton>
                <AppButton
                  block
                  variant="outline"
                  onClick={() => { setAppealModal(null); setAppealReason(""); setAppealFile(null); }}
                >
                  {t("cancel")}
                </AppButton>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de apelación — versión desktop */}
      {appealModal && !isMobile && (
        <div style={{ position: "fixed", inset: 0, background: appColors.scrim, display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }} onClick={() => { setAppealModal(null); setAppealReason(""); setAppealFile(null); }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }} onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: "#fffbeb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FaGavel size={16} color="#b45309" />
                </div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 400, color: "#1a1a2e" }}>{t("appealFine")}</h3>
              </div>
              <button onClick={() => { setAppealModal(null); setAppealReason(""); setAppealFile(null); }} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                <FaTimes size={16} color="#9ca3af" />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "16px 24px" }}>
              {/* Resumen de la multa */}
              <div style={{ background: "#f9fafb", borderRadius: 8, padding: "12px 14px", marginBottom: 16, fontSize: 13 }}>
                <div style={{ color: "#374151", marginBottom: 4 }}><strong>{t("motiveLabel")}:</strong> {appealModal.reason}</div>
                <div style={{ color: "#374151", marginBottom: 4 }}><strong>{t("montoLabel")}:</strong> {formatCurrencyUY(appealModal.amount || 0)}</div>
                <div style={{ color: "#9ca3af" }}>{formatDate(appealModal.createdAt)}{appealModal.legalReference ? ` · ${appealModal.legalReference}` : ""}</div>
              </div>

              {/* Info */}
              <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#1e40af", marginBottom: 16, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <FaInfoCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>{t("appealInfo")}</span>
              </div>

              {/* Textarea */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 400, color: "#374151", display: "block", marginBottom: 4 }}>
                  {t("justificationLabel")}
                </label>
                <textarea
                  value={appealReason}
                  onChange={(e) => setAppealReason(e.target.value)}
                  placeholder={t("justificationPlaceholder")}
                  rows={4}
                  style={{ width: "100%", padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", outline: "none" }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = "#0080ff"; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = "#d1d5db"; }}
                />
                <span style={{ fontSize: 11, color: appealReason.trim().length >= 20 ? "#16a34a" : "#9ca3af" }}>
                  {t("minChars", { n: appealReason.trim().length })}
                </span>
              </div>

              {/* Upload de evidencia */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 400, color: "#374151", display: "block", marginBottom: 4 }}>
                  {t("evidenceLabel")}
                </label>
                {appealFile ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", border: "1px solid #d1d5db", borderRadius: 8, background: "#f9fafb" }}>
                    <FaFileAlt size={14} color="#0080ff" />
                    <span style={{ flex: 1, fontSize: 13, color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {appealFile.name}
                    </span>
                    <button onClick={() => setAppealFile(null)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                      <FaTimes size={12} color="#6b7280" />
                    </button>
                  </div>
                ) : (
                  <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", border: "1px dashed #cbd5e1", borderRadius: 8, cursor: "pointer", background: "#f8fafc", color: "#64748b", fontSize: 13 }}>
                    <FaCloudUploadAlt size={16} />
                    <span>{t("attachEvidence")}</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" onChange={handleAppealFileChange} style={{ display: "none" }} />
                  </label>
                )}
                <span style={{ fontSize: 11, color: "#9ca3af" }}>{t("maxSize")}</span>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: "12px 24px 20px", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => { setAppealModal(null); setAppealReason(""); setAppealFile(null); }}
                style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #d1d5db", background: "#fff", color: "#374151", fontSize: 13, fontWeight: 400, cursor: "pointer" }}
              >
                {t("cancel")}
              </button>
              <button
                onClick={handleSubmitAppeal}
                disabled={submittingAppeal || appealReason.trim().length < 20}
                style={{
                  padding: "8px 16px", borderRadius: 8, border: "none",
                  background: submittingAppeal || appealReason.trim().length < 20 ? "#d1d5db" : "#b45309",
                  color: "#fff", fontSize: 13, fontWeight: 400, cursor: submittingAppeal || appealReason.trim().length < 20 ? "not-allowed" : "pointer",
                }}
              >
                {submittingAppeal ? t("sending") : t("submitAppeal")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MultasResidente;
